import { Button, Heading } from "@ariakit/react";
import {
  getFetchFailureMessages,
  type DatabaseQueryOpFailure,
  type FailurePayload,
  type FetchFailureAction,
  type FetchFailureMessages,
} from "@indietabletop/appkit";
import { captureException } from "@sentry/react";
import { useEffect } from "react";
import { Link } from "wouter";
import { button } from "../../common/common.css.ts";
import type { GangNotFound } from "../../database.ts";
import type { GitNotFound, VehicleNotFound } from "../../engine/Gang.ts";
import type { DocContentTypeMismatch } from "../HelpPages/DocsPages.tsx";
import { failureView } from "./style.css.ts";

type BadPath = {
  type: "UNKNOWN_PATH";
};

export type KnownFailure =
  | BadPath
  | FailurePayload
  | GangNotFound
  | VehicleNotFound
  | GitNotFound
  | DatabaseQueryOpFailure
  | DocContentTypeMismatch;

function getFailureMessages(
  failure: KnownFailure,
  messages?: Record<number, { title?: string; description?: string }>
): FetchFailureMessages {
  switch (failure.type) {
    case "DB_VERSION_HIGHER_THAN_REQUESTED":
    case "CLOSED_FOR_UPGRADE":
    case "UPGRADE_BLOCKED":
    case "QUERY_ERROR": {
      return {
        title: `Local database inaccessible`,
        description: `Could not reach local database (code: ${failure.type}). Try refreshing the page.`,
        action: { type: "RELOAD", label: "Reload" },
      };
    }

    case "GANG_NOT_FOUND": {
      return {
        title: `Gang not found`,
        description: `The gang might have been removed, or the link you have followed might be broken.`,
        action: { type: "LINK", href: "~/", label: "Go back" },
      };
    }

    case "VEHICLE_NOT_FOUND": {
      return {
        title: `Vehicle not found`,
        description: `The vehicle might have been removed, or the link you have followed might be broken.`,
        action: { type: "LINK", href: "/", label: "Go back" },
      };
    }

    case "GIT_NOT_FOUND": {
      return {
        title: `Git not found`,
        description: `The git might have been removed, or the link you have followed might be broken.`,
        action: { type: "LINK", href: "/", label: "Go back" },
      };
    }

    // Happen due to Netlify rewriting /<doc>.json to /index.html. Conceptually
    // this is basically identical to UNKNOWN_PATH.
    case "DOC_CONTENT_TYPE_MISMATCH":
    case "UNKNOWN_PATH": {
      return {
        title: `Nothing here`,
        description: `The link you have followed might be broken.`,
        action: { type: "LINK", href: "~/", label: "Go back" },
      };
    }
  }

  return getFetchFailureMessages(failure, messages);
}

function useReportFailure(failure: KnownFailure) {
  useEffect(() => {
    if (
      failure.type === "UNKNOWN_ERROR" ||
      failure.type === "UNKNOWN_PATH" ||
      failure.type === "QUERY_ERROR" ||
      failure.type === "DOC_CONTENT_TYPE_MISMATCH" ||
      failure.type === "VALIDATION_ERROR" ||
      (failure.type === "API_ERROR" && failure.code >= 500)
    ) {
      captureException(failure);
    }
  }, [failure]);
}

function FailureButton(props: {
  action: FetchFailureAction;
  className?: string;
}) {
  const { action, className } = props;

  switch (action.type) {
    case "LINK": {
      return (
        <Link className={className} href={action.href}>
          {action.label}
        </Link>
      );
    }

    case "RELOAD":
      return (
        <Button
          className={className}
          // Reload and redirect to root
          onClick={() => void window.location.replace("/")}
        >
          {action.label}
        </Button>
      );

    case "REFETCH": {
      return (
        <Button
          className={className}
          // Reload current page
          onClick={() => void window.location.reload()}
        >
          {action.label}
        </Button>
      );
    }
  }
}

export function FailureView(props: {
  failure: KnownFailure;
  messages?: Record<number, { title?: string; description?: string }>;
}) {
  const { failure, messages } = props;
  const { title, description, action } = getFailureMessages(failure, messages);

  useReportFailure(failure);

  return (
    <div className={failureView.container}>
      <Heading className={failureView.heading}>{title}</Heading>
      <p className={failureView.description}>{description}</p>

      <div className={failureView.actions}>
        <FailureButton
          action={action}
          className={button({ marginInline: "auto", size: "small" })}
        />
      </div>
    </div>
  );
}
